/*
  This class represents media elements like video textures rendered onto a 
  grid system in a Three.js scene. It uses GPGPU computation for advanced 
  graphical effects and interacts with a grid of cells that respond to user 
  mouse input. The media is dynamically resized and positioned based on the 
  element’s size and the current viewport.
*/

import * as THREE from 'three'
import GPGPU, { getGridSize } from './displacementField'
import { Size } from './types'
import { createDeformationShader } from './shaders'
import { CellGridSystem } from './trailCells'

interface Props {
  element: HTMLVideoElement
  scene: THREE.Scene
  sizes: Size
  renderer: THREE.WebGLRenderer
}

export default class ShowreelMedia {
  videoElement: HTMLVideoElement    // The media video element
  scene: THREE.Scene                // The Three.js scene in which the media is rendered
  sizes: Size                       // This represents the width and height of the media
  videoTexture!: THREE.VideoTexture // Texture generated from the video element
  hoverTexture: THREE.Texture | null = null
  material!: THREE.ShaderMaterial   // Shader material for custom effects
  geometry!: THREE.PlaneGeometry    // Plane geometry to display the video
  renderer: THREE.WebGLRenderer     // The WebGL renderer used to display the scene
  gridSize!: THREE.Vector2          // Grid size for GPGPU and deformation effects
  mesh!: THREE.Mesh                 // Mesh that holds the video texture and shader material
  nodeDimensions!: Size             // Dimensions of the media element
  meshDimensions!: Size             // Scaled dimensions of the mesh
  elementBounds!: DOMRect           // DOM bounds of the video element
  gpgpu!: GPGPU                     // GPGPU instance for complex rendering tasks
  time: number                      // Timestamp for handling frame steps
  cellGridSystem: CellGridSystem    // Instance of the CellGridSystem for managing grid cells

  constructor({ element, scene, sizes, renderer }: Props) {
    this.videoElement = element
    this.scene = scene
    this.sizes = sizes
    this.renderer = renderer
    this.time = 0

    // Initialize the components and add the mesh to the scene
    this.createGeometry()
    this.createMaterial()
    this.createMesh()
    this.setNodeBounds()
    this.setMeshDimensions()
    this.setMeshPosition()
    this.setTexture()
    const aspect = window.innerWidth / window.innerHeight
    this.createGPGPU(aspect)
    this.setGridSize(aspect)
    
    // Initialize the CellGridSystem for grid interaction
    this.cellGridSystem = new CellGridSystem(this.gridSize)
    this.scene.add(this.mesh)
  }

  // Create the geometry for the video plane
  createGeometry() {
    this.geometry = new THREE.PlaneGeometry(1, 1)
  }

  // Create the shader material for custom effects and set up the video texture
  createMaterial() {
    const shader = createDeformationShader();
    this.material = new THREE.ShaderMaterial({
      uniforms: shader.uniforms,
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
      defines: {
        PR: Math.min(2, window.devicePixelRatio)
      }
    })
    this.videoTexture = new THREE.VideoTexture(this.videoElement)
  }

  // Create the mesh using the plane geometry and shader material
  createMesh() {
    this.mesh = new THREE.Mesh(this.geometry, this.material)
  }

  createGPGPU(aspect: number) {
    this.gpgpu = new GPGPU({
      renderer: this.renderer,
      aspect,
    })
  }

  setGridSize(aspect: number) {
    const { rows } = getGridSize(aspect)
    this.gridSize = new THREE.Vector2(rows * aspect, rows)
    this.material.uniforms.uGridSize.value = this.gridSize
  }

  // Get the bounds of the DOM element
  setNodeBounds() {
    this.elementBounds = this.videoElement.getBoundingClientRect()

    this.nodeDimensions = {
      width: this.elementBounds.width,
      height: this.elementBounds.height,
    }
  }

  // Set the dimensions of the mesh based on the node and window size
  setMeshDimensions() {
    this.meshDimensions = {
      width: (this.nodeDimensions.width * this.sizes.width) / window.innerWidth,
      height: (this.nodeDimensions.height * this.sizes.height) / window.innerHeight,
    }

    this.mesh.scale.x = this.meshDimensions.width
    this.mesh.scale.y = this.meshDimensions.height
  }

  // Position the mesh within the scene
  setMeshPosition() {
    this.mesh.position.x = 0
    this.mesh.position.y = 0
  }

  // Set the textures for the material
  setTexture() {
    this.material.uniforms.uTexture.value = this.videoTexture

    // Optionally load a hover texture if available
    const textureHoverData = this.videoElement.dataset.hover
    if (textureHoverData) {
      this.hoverTexture = new THREE.TextureLoader().load(textureHoverData)
      this.material.uniforms.uTextureHover.value = this.hoverTexture
    }
  }

  // Handle resizing and update the mesh dimensions accordingly
  onResize(sizes: Size) {
    this.sizes = sizes

    this.setNodeBounds()
    this.setMeshDimensions()
    this.material.uniforms.uContainerResolution.value = new THREE.Vector2(window.innerWidth, window.innerHeight)
    const aspect = window.innerWidth / window.innerHeight
    this.setGridSize(aspect)
    this.cellGridSystem.setGridSize(this.gridSize);

    const { columns, rows } = getGridSize(aspect)
    if (columns !== this.gpgpu.gridSize.columns || rows !== this.gpgpu.gridSize.rows) {
      this.gpgpu.dispose()
      this.createGPGPU(aspect)
    }
  }

  // Handle pointer movement and update the GPGPU and grid system accordingly
  onPointerMove(uv: THREE.Vector2) {
    this.gpgpu.updateMouse(uv)
    this.cellGridSystem.addCell(uv)
  }

  // Render function called on each animation frame
  render(dt: number) {
    this.time += dt

    this.gpgpu.render(dt)

    // Update the cells in the grid system
    this.cellGridSystem.tick(dt)

    this.material.uniforms.uTime.value = this.time * 5.0

    // Update shader uniforms with the latest grid and GPGPU data
    this.material.uniforms.uCellData.value = this.cellGridSystem.getShaderData()
    this.material.uniforms.uGrid.value = this.gpgpu.getTexture()
  }

  dispose() {
    this.geometry.dispose()
    this.material.dispose()
    this.videoTexture.dispose()
    this.hoverTexture?.dispose()
    this.gpgpu.dispose()
  }
}
